import {supabase} from '../supabase/client';

const HEALTH_TABLE = 'business_health_events';

function assertSupabase() {
  if (!supabase) throw new Error('Supabase is not configured.');
}

export async function recordHealthEvent(businessId, category, outcome, eventCode, route) {
  if (!supabase || !businessId) return false;
  const {error} = await supabase.rpc('record_business_health_event', {
    p_business_id: businessId,
    p_category: category,
    p_outcome: outcome,
    p_event_code: eventCode,
    p_route: route
  });
  if (error && import.meta.env.DEV) console.warn('Health event was not recorded:', error.message);
  if (!error && outcome === 'failure') void flushHealthAlerts(businessId);
  return !error;
}

export async function flushHealthAlerts(businessId) {
  if (!supabase || !businessId) return {processed: 0, sent: 0};
  const {data, error} = await supabase.functions.invoke('send-launch-alert', {body: {business_id: businessId}});
  if (error && import.meta.env.DEV) console.warn('Launch alerts were not processed:', error.message);
  return data || {processed: 0, sent: 0};
}

export async function updateBusinessHealthIncident(incidentId, action) {
  assertSupabase();
  const {error} = await supabase.rpc('update_business_health_incident', {
    p_incident_id: incidentId,
    p_action: action
  });
  if (error) throw new Error(`Unable to update incident: ${error.message}`);
  return true;
}

export async function getBusinessHealth(businessId) {
  assertSupabase();
  const since = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
  const [{data: events, error: eventsError}, {data: incidents, error: incidentsError}, subscription] = await Promise.all([
    supabase.from(HEALTH_TABLE)
      .select('id,category,outcome,event_code,route,created_at')
      .eq('business_id', businessId)
      .gte('created_at', since)
      .order('created_at', {ascending: false})
      .limit(100),
    supabase.from('business_health_incidents')
      .select('id,category,event_code,severity,status,failure_count,first_seen_at,last_seen_at,acknowledged_at,resolved_at,resolution_code')
      .eq('business_id', businessId)
      .order('last_seen_at', {ascending: false})
      .limit(50),
    supabase.from('subscriptions_1772930000').select('*').eq('business_id', businessId).maybeSingle().then(({data,error}) => { if (error) throw error; return data; })
  ]);
  if (eventsError || incidentsError) throw new Error(`Unable to load production health: ${(eventsError || incidentsError).message}`);
  return {events: events || [], incidents: incidents || [], subscription};
}

export function summarizeBusinessHealth(events, subscription, now = Date.now()) {
  const dayAgo = now - 24 * 60 * 60 * 1000;
  const recent = events.filter((event) => new Date(event.created_at).getTime() >= dayAgo);
  const failures = recent.filter((event) => event.outcome === 'failure');
  const failuresFor = (category) => failures.filter((event) => event.category === category).length;
  const billingHealthy = !subscription || ['active', 'trialing'].includes(subscription.status);
  return {
    overall: failures.length === 0 && billingHealthy ? 'healthy' : 'attention',
    failures24h: failures.length,
    auth: {status: 'healthy', failures: failuresFor('auth')},
    billing: {status: billingHealthy && failuresFor('billing') === 0 ? 'healthy' : 'attention', failures: failuresFor('billing')},
    invoice: {status: failuresFor('invoice') === 0 ? 'healthy' : 'attention', failures: failuresFor('invoice')},
    workflow: {status: failuresFor('workflow') === 0 ? 'healthy' : 'attention', failures: failuresFor('workflow')}
  };
}
